import { AfterViewInit, Component, ErrorHandler, OnDestroy, OnInit, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { Validators } from '@angular/forms'
import { disabled, FieldTree, form, required } from '@angular/forms/signals'
import { MatSnackBar } from '@angular/material/snack-bar'
import {
  ApixDownloadLaskutustiedotPdfRequest,
  ApixDownloadLaskutustiedotPdfResponse,
  ApixReceivedInvoiceConfig,
  ApixReceivedInvoiceConfigUpdateRequest,
  ApixReceivedInvoiceConfigUpdateResponse,
} from '@lemon/core-firebase/model/apix'
import { LaskunSahkoinenOsoite } from '@lemon/core-firebase/model/lasku'
import { SahkoisenLaskunValittajaService, Verkkolaskuoperaattori } from '@lemon/core-firebase/service/lasku/sahkoisen-laskun-valittaja.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { KayttajaService } from 'app/_angular/service/kayttaja.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from 'app/_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from 'app/_jaettu-angular/service/lemon-translation.service'
import { firstValueFrom, map, Observable, of as observableOf, Subject, switchMap, take, takeUntil } from 'rxjs'

import { NewFeature } from '@lemon/core-firebase/model/kayttaja'
import { UserSettingsService } from 'app/_angular/service/user-settings.service'
import { FileSaverService } from 'app/_jaettu-angular/service/file-saver'
import { TiedostojenLataamisService } from '../../_jaettu-angular/service/tiedostojen-lataamis.service'

interface VastaanottoLomake {
  serviceActive: boolean | null
  valittaja: string | null
  osoite: string
  forwardLemontreeLaskut: boolean | null
  forwardRequiresApproving: boolean | null
}

// _sahkoisenLaskunValittajaService
interface LaskunSahkoinenOsoiteJaNimi extends LaskunSahkoinenOsoite {
  sahkoinenValittajaNimi: string
}

@Component({
  selector: 'app-laskujen-vastaanotto',
  templateUrl: './laskujen-vastaanotto.component.html',
  styleUrls: ['./laskujen-vastaanotto.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LaskujenVastaanottoComponent implements OnInit, OnDestroy, AfterViewInit {
  readonly viewState = signal<'not-enabled' | 'edit-address' | 'enabled' | null>(null)

  readonly noApixAddressYet = signal(true)

  readonly commonError = signal<string | null>(null)

  lomake = form(signal<VastaanottoLomake>({ serviceActive: null, valittaja: null, osoite: '', forwardLemontreeLaskut: null, forwardRequiresApproving: null }), polku => {
    validoiReaktiivisesti(polku.serviceActive, Validators.requiredTrue)
    disabled(polku.valittaja, ({ valueOf }) => !valueOf(polku.serviceActive))
    required(polku.valittaja)
    disabled(polku.osoite, ({ valueOf }) => !valueOf(polku.serviceActive))
    required(polku.osoite)
    disabled(polku.forwardLemontreeLaskut, ({ valueOf }) => !valueOf(polku.serviceActive))
    validoiReaktiivisesti(polku.forwardLemontreeLaskut, Validators.required)
    disabled(polku.forwardRequiresApproving, ({ valueOf }) => !valueOf(polku.serviceActive))
    validoiReaktiivisesti(polku.forwardRequiresApproving, Validators.required)
  })

  operaattorit: Verkkolaskuoperaattori[]

  apixLaskuosoiteObservable: Observable<LaskunSahkoinenOsoiteJaNimi>
  laskujenHyvaksyntaActiveObservable: Observable<boolean>

  private _cachedApixConfig: Pick<ApixReceivedInvoiceConfig, 'forwardRequiresApproving' | 'forwardLemontreeLaskut' | 'forwardAddress'> = {
    forwardRequiresApproving: null,
    forwardLemontreeLaskut: null,
    forwardAddress: {
      sahkoinenOsoite: null,
      sahkoinenValittaja: null,
    },
  }

  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  private _saveStarted: boolean = false

  private readonly _injector = inject(Injector)

  constructor(
    private _formValidationService: FormValidationService,
    private _ladataanService: LadataanService,
    private _errorHandler: ErrorHandler,
    private _translationService: LemonTranslationService,
    private _sahkoisenLaskunValittajaService: SahkoisenLaskunValittajaService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _kayttajaService: KayttajaService,
    private _snackBar: MatSnackBar,
    private _fileSaverService: FileSaverService,
    private _userSettingsService: UserSettingsService,
    private _tiedostojenLataamisService: TiedostojenLataamisService,
  ) {}

  ngOnInit(): void {
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector })

    muutokset(this.valittaja)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(val => {
        if (!!val) {
          this._cachedApixConfig.forwardAddress.sahkoinenValittaja = val
        }
      })

    muutokset(this.osoite)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(val => {
        this._cachedApixConfig.forwardAddress.sahkoinenOsoite = val
      })

    muutokset(this.forwardLemontreeLaskut)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(val => {
        this._cachedApixConfig.forwardLemontreeLaskut = val
      })

    muutokset(this.forwardRequiresApproving)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(val => {
        this._cachedApixConfig.forwardRequiresApproving = val
      })

    this.operaattorit = this._sahkoisenLaskunValittajaService.annaKaikkiValittajat()

    const apixInvoiceConfigObservable = this._kayttajaService.kayttajanTiedotObservable.pipe(
      switchMap(kayttajanTiedot => {
        if (!kayttajanTiedot?.asiakasAvain) {
          return observableOf(null)
        }
        return this._firebaseLemonaid.firestoreDoc<ApixReceivedInvoiceConfig>('customers/' + kayttajanTiedot.asiakasAvain + '/apix-received-invoice-config/' + kayttajanTiedot.asiakasAvain).listen()
      }),
    )

    this.apixLaskuosoiteObservable = apixInvoiceConfigObservable.pipe(
      map(config => {
        if (config?.receiveActive && config?.receiveAddress) {
          const osoiteJaNimi: LaskunSahkoinenOsoiteJaNimi = {
            sahkoinenOsoite: config.receiveAddress.sahkoinenOsoite,
            sahkoinenValittaja: config.receiveAddress.sahkoinenValittaja,
            sahkoinenValittajaNimi: this._sahkoisenLaskunValittajaService.annaValittajanNimi(config.receiveAddress.sahkoinenValittaja),
          }
          return osoiteJaNimi
        }
        return null
      }),
    )

    this.laskujenHyvaksyntaActiveObservable = apixInvoiceConfigObservable.pipe(
      map(config => {
        return config?.forwardActive && config?.forwardRequiresApproving
      }),
    )

    apixInvoiceConfigObservable.pipe(take(1)).subscribe(config => {
      /** View won't show anything until the data has been loaded */
      this.viewState.set(config?.receiveActive && config?.receiveAddress ? 'enabled' : 'not-enabled')

      asetaArvo(this.forwardLemontreeLaskut, config?.forwardLemontreeLaskut ?? true)
      asetaArvo(this.forwardRequiresApproving, config?.forwardRequiresApproving ?? false)

      if (config?.receiveActive) {
        this.noApixAddressYet.set(false) // Toggle the buttons that are shown on the edit page

        // Form change will also propagate _cachedApixConfig update
        asetaArvo(this.serviceActive, true)
        asetaArvo(this.osoite, config.forwardAddress.sahkoinenOsoite ?? '')
        asetaArvo(this.valittaja, config.forwardAddress.sahkoinenValittaja)
      }
    })
  }

  ngAfterViewInit(): void {
    // this.updateSeen(NewFeature.LASKUJEN_VASTAANOTTO)
  }

  get serviceActive() {
    return this.lomake.serviceActive
  }

  get valittaja() {
    return this.lomake.valittaja
  }

  get osoite() {
    return this.lomake.osoite
  }

  get forwardLemontreeLaskut() {
    return this.lomake.forwardLemontreeLaskut
  }

  get forwardRequiresApproving() {
    return this.lomake.forwardRequiresApproving
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  editAddress() {
    this.viewState.set('edit-address')
  }

  backToEnabledView() {
    this.viewState.set('enabled')
  }

  async saveAddress() {
    if (!this.lomake().valid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    if (this._saveStarted) {
      return
    }

    this._saveStarted = true
    this._ladataanService.aloitaLataaminen()

    try {
      const reqData: ApixReceivedInvoiceConfigUpdateRequest = {
        forwardAddress: this._cachedApixConfig.forwardAddress,
        forwardLemontreeLaskut: this._cachedApixConfig.forwardLemontreeLaskut,
        forwardRequiresApproving: this._cachedApixConfig.forwardRequiresApproving,
      }

      const resp = await this._firebaseLemonaid.functionsCall<ApixReceivedInvoiceConfigUpdateRequest, ApixReceivedInvoiceConfigUpdateResponse>('updateReceivedInvoiceConfig', reqData)

      if (!resp || resp.e) {
        /** Throw error to end up in the catch. */
        throw new Error('Forwarded address save failed! ' + (resp?.e || 'unknown-error'))
      }

      /** All is fine, switch view */
      this.viewState.set('enabled')
    } catch (err) {
      this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe'))
      this._errorHandler.handleError(err)
    } finally {
      this._saveStarted = false
      this._ladataanService.lopetaLataaminen()
    }
  }

  async downloadPdf() {
    this._ladataanService.aloitaLataaminen()

    try {
      const asiakas = await firstValueFrom(this._kayttajaService.nykyinenAsiakasObservable)

      const reqData: ApixDownloadLaskutustiedotPdfRequest = {
        kieli: 'fi',
      }

      const resp = await this._firebaseLemonaid.functionsCall<ApixDownloadLaskutustiedotPdfRequest, ApixDownloadLaskutustiedotPdfResponse>('downloadLaskutustiedotPdf', reqData)

      if (!resp || resp.e) {
        throw new Error('Laskutustiedot PDF download failed! ' + (resp?.e ?? 'No response data')) // Handle in catch
      }

      if (!resp.base64File?.length) {
        throw new Error('Laskutustiedot PDF download failed! PDF file empty')
      }

      const uint8Array = this._tiedostojenLataamisService.base64ToUint8Array(resp.base64File)
      const laskutustiedotLocalised = this._translationService.lokalisoiKielella('laskutustiedot-pdf.laskutustiedot', reqData.kieli)
      const fileName = laskutustiedotLocalised + ' ' + asiakas.nimi + ' (' + asiakas.ytunnus + ')' + '.pdf'
      this._fileSaverService.saveAs(new Blob([uint8Array], { type: 'application/pdf' }), fileName)
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackBar.open(this._translationService.lokalisoi('tositteet.laskujen-vastaanotto.pdf-epaonnistui'), 'OK', { duration: 10 * 1000 })
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  async updateSeen(seenFeature: NewFeature) {
    const kayttajanTiedot = await firstValueFrom(this._kayttajaService.kayttajanTiedotObservable)
    if (!kayttajanTiedot?.asiakasAvain) {
      return
    }

    return this._userSettingsService.setFeatureSeen(seenFeature, kayttajanTiedot.asiakasAvain, kayttajanTiedot.uid, this._firebaseLemonaid)
  }
}
